// src/FlowBuilder.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom'; // capturar o ID

import CodeBuilder from './Codes';
import ToolsBuilder from './Tools';

import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges,
    Handle,
    Position,
    BaseEdge,
    EdgeLabelRenderer,
    getStraightPath,
    useReactFlow,
    getBezierPath,
    useUpdateNodeInternals
} from 'reactflow';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import 'reactflow/dist/style.css';
import { ButtonGroup, TextField, DialogActions, DialogContent, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import api from '../../../../services/api';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { makeStyles } from '@mui/styles';
import './NodeStyle.css';
import Editor from '@monaco-editor/react';
import CodeIcon from '@mui/icons-material/Code';

// ---------- Helpers ----------
const STORAGE_KEY = 'botflow_v1';

const uid = () => Math.random().toString(36).slice(2, 9);

function DraggableItem({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="bp-item">
            <span className="bp-drag">☰</span>
            <div className="bp-content">{children}</div>
        </div>
    );
}

function CustomEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd }) {
    const { deleteElements } = useReactFlow();

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition
    });

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={{ strokeWidth: 2 }} />

            <EdgeLabelRenderer>
                <button
                    className="nodrag nopan"
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                        pointerEvents: 'all',
                        zIndex: 50,
                        background: '#222',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '2px 6px',
                        cursor: 'pointer',
                        fontSize: 12
                    }}
                    onClick={() => deleteElements({ edges: [{ id }] })}
                >
                    ✕
                </button>
            </EdgeLabelRenderer>
        </>
    );
}

const edgeTypes = { custom: CustomEdge };

const useStyles = makeStyles((theme) => ({
    bpNode: {
        background: theme.palette.background.paper, //'#f9fafb',
        color: theme.palette.text.primary, //'#11181c',
        borderRadius: 8,
        border: '1px solid #d0d7de',
        minWidth: 260,
        fontSize: 13
    },

    bpHeader: {
        padding: '8px 10px',
        fontWeight: 600,
        background: '#eef2f7',
        borderBottom: '1px solid #d0d7de'
    },

    bpSection: {
        padding: 8
    },

    bpTitle: {
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 6,
        color: '#555'
    },

    bpItem: {
        display: 'flex',
        alignItems: 'center',
        background: '#fff',
        border: '1px solid #e1e4e8',
        borderRadius: 6,
        padding: 6,
        marginBottom: 6,
        cursor: 'grab',

        '&:hover': {
            background: '#f0f4ff'
        }
    },

    bpDrag: {
        marginRight: 6,
        color: '#999',
        cursor: 'grab'
    },

    bpContent: {
        flex: 1
    }
}));

const truncateText = (text, max = 25) => {
    if (!text) return '';
    return text.length > max ? text.slice(0, max) + '...' : text;
};

function MessageNode({ id, data }) {
    const entradas = data?.children?.entrada || [];
    const processamento = data?.children?.processamento || [];
    const saidas = data?.children?.saida || [];

    const updateChildrenOrder = (side, newOrder) => {
        data.onUpdateChildren(id, {
            ...data.children,
            [side]: newOrder
        });
    };

    return (
        <div className="bp-node">
            <div className="bp-header">{data.name}</div>

            {/* ENTRADA */}
            <div className="bp-section">
                <div className="bp-title">Entrada</div>

                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => {
                        const { active, over } = e;
                        if (!over || active.id === over.id) return;

                        const oldIndex = entradas.findIndex((i) => i.id === active.id);
                        const newIndex = entradas.findIndex((i) => i.id === over.id);

                        const reordered = [...entradas];
                        const [moved] = reordered.splice(oldIndex, 1);
                        reordered.splice(newIndex, 0, moved);

                        updateChildrenOrder('entrada', reordered);
                    }}
                >
                    <SortableContext items={entradas.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                        {entradas.map((item) => (
                            <DraggableItem key={item.id} id={item.id}>
                                {item.type === 'text' && <span title={item.text}>{truncateText(item.text, 25)}</span>}

                                {item.type === 'regex' && <span style={{ color: '#7c3aed' }}>🧠 regex → {item.variable}</span>}

                                {item.action === 'codigo' && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <CodeIcon fontSize="small" />
                                        {item.code_name}
                                    </span>
                                )}

                                {/* {item.type === 'handoff' && (
                                    <span style={{ color: '#7c3aed' }}> Transferencia de canal{item.variable}</span>
                                )} */}
                                {item.type === 'handoff' && `Transferir → ${item.target}`}
                                {/* {item.type === 'ai_agent' && `🤖 I.A`} */}
                            </DraggableItem>
                        ))}
                    </SortableContext>
                </DndContext>

                <Handle type="target" position={Position.Left} />
            </div>

            {/* Processamento */}
            <div className="bp-section">
                <div className="bp-title">Processamento</div>

                {data.waitForUser && (
                    <div className="bp-wait">
                        {/* {data.name} */}
                        {data.waitForUser && <span style={{ marginLeft: 6 }}>Aguardar resposta do usuário ⏳</span>}
                    </div>
                )}

                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => {
                        const { active, over } = e;
                        if (!over || active.id === over.id) return;

                        const oldIndex = processamento.findIndex((i) => i.id === active.id);
                        const newIndex = processamento.findIndex((i) => i.id === over.id);

                        const reordered = [...processamento];
                        const [moved] = reordered.splice(oldIndex, 1);
                        reordered.splice(newIndex, 0, moved);

                        updateChildrenOrder('processamento', reordered);
                    }}
                >
                    <SortableContext items={processamento.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                        {processamento.map((item) => (
                            <DraggableItem key={item.id} id={item.id}>
                                {item.type === 'text' && <span title={item.text}>{truncateText(item.text, 25)}</span>}

                                {item.action === 'codigo' && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <CodeIcon fontSize="small" />
                                        {item.code_name}
                                    </span>
                                )}

                                {/* {item.type === 'handoff' && (
                                    <span style={{ color: '#7c3aed' }}> Transferencia de canal{item.variable}</span>
                                )} */}
                                {item.type === 'handoff' && `Transferir → ${item.target}`}
                                {/* {item.type === 'ai_agent' && `🤖 I.A`} */}
                            </DraggableItem>
                        ))}
                    </SortableContext>
                </DndContext>

                <Handle type="target" position={Position.Left} />
            </div>

            {/* SAÍDA */}
            <div className="bp-section">
                <div className="bp-title">Saída</div>

                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => {
                        const { active, over } = e;
                        if (!over || active.id === over.id) return;

                        const oldIndex = saidas.findIndex((i) => i.id === active.id);
                        const newIndex = saidas.findIndex((i) => i.id === over.id);

                        const reordered = [...saidas];
                        const [moved] = reordered.splice(oldIndex, 1);
                        reordered.splice(newIndex, 0, moved);

                        updateChildrenOrder('saida', reordered);
                    }}
                >
                    <SortableContext items={saidas.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                        {saidas.map((item, idx) => (
                            <div key={item.id} className="bp-output-row">
                                <DraggableItem id={item.id}>
                                    {item.type === 'text' && <span title={item.text}>{truncateText(item.text, 25)}</span>}

                                    {item.action === 'codigo' && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <CodeIcon fontSize="small" />
                                            {item.code_name}
                                        </span>
                                    )}

                                    {/* {item.type === 'handoff' && (
                                        <span style={{ color: '#7c3aed' }}> Transferencia de canal{item.variable}</span>
                                    )} */}
                                    {item.type === 'handoff' && `Transferir → ${item.target}`}
                                    {/* {item.type === 'ai_agent' && `🤖 I.A`} */}
                                </DraggableItem>

                                <Handle
                                    id={`${id}-out-${item.id}`}
                                    type="source"
                                    position={Position.Right}
                                    style={{ top: 28 + idx * 42 }}
                                />
                            </div>
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
}

const nodeTypes = {
    message: MessageNode
};

const createAction = (type, config) => ({
    id: uid(),
    type,
    config
});

// ---------- Main FlowBuilder ----------
export default function FlowBuilder() {
    // console.log(`FlowBuilder - Entrou`);
    const { id } = useParams(); // ID da URL
    const [appId, setAppId] = useState(null);
    const [switchboards, setSwitchboards] = useState([]);
    const [handoffEditing, setHandoffEditing] = useState(null);

    const [colorSave, setColorSave] = useState('primary');

    const classes = useStyles();

    const load_app = async () => {
        console.log(`entrou em load_app`);
        const resp = await api.get(`/v1/apps/default/`);
        if (resp.data) {
            console.log(`load_app - resp.data: ${JSON.stringify(resp.data)}`);
            setAppId(resp.data.app.id);
        }
    };

    const loadInitial = async () => {
        console.log(`loadInitial - entrou`);
        setLoading(true);
        try {
            if (!appId) return;

            const res = await api.get(`v1/apps/${appId}/chatbots/${id}/flows/`);

            if (res.data && res.data.length > 0) {
                console.log(`loadInitial - res.data: ${JSON.stringify(res.data)}`);
                const flow = res.data[0];
                setIdFlow(flow.id);
                // setNodes(flow.nodes || []);
                const normalizedNodes = (flow.nodes || []).map((n) => ({
                    ...n,
                    data: {
                        name: n.data?.name || `Caixa ${n.id}`,
                        previewText: n.data?.previewText || '',
                        allowTarget: n.id !== 'main',
                        waitForUser: n.data?.waitForUser ?? false,
                        children: {
                            entrada: n.data?.children?.entrada || [],
                            processamento: n.data?.children?.processamento || [],
                            saida: n.data?.children?.saida || []
                        }
                    }
                }));

                setNodes(normalizedNodes);
                setEdges(flow.edges || []);
            } else {
                // fallback se não existir flow
                setNodes([
                    {
                        id: 'main',
                        type: 'message',
                        position: { x: 0, y: 0 },
                        data: {
                            name: 'Main (Início)',
                            previewText: 'Este é o nó inicial.',
                            allowTarget: false,
                            waitForUser: false,
                            children: {
                                entrada: [],
                                saida: [{ id: uid(), type: 'text', text: 'Primeira saída' }]
                            }
                        }
                    }
                ]);
                setEdges([]);
            }
        } catch (err) {
            console.error('Erro ao carregar flow', err);
        } finally {
            setLoading(false);
        }
    };

    const load_sb_integration = async () => {
        console.log(`load_sb_integration - entrou`);
        const res = await api.get(`v1/apps/${appId}/switchboardIntegrations/`);
        if (res.data) {
            console.log(`load_sb_integration - res.data:${JSON.stringify(res.data)}`);
            setSwitchboards(res.data.switchboardIntegrations);
        }
    };

    useEffect(() => {
        load_app();
    }, []);

    useEffect(() => {
        if (!appId) return;
        load_sb_integration();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        loadInitial();
    }, [id, appId]);

    // UI state
    const [selectedNode, setSelectedNode] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [modal, setModal] = useState(null); // { side: 'entrada'|'saida', nodeId }
    const [modalType, setModalType] = useState('choose'); // 'choose'|'text'|'action'
    const [tempText, setTempText] = useState('');
    const [editingName, setEditingName] = useState('');

    const [aiTempText, setAiTempText] = useState('');

    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [idFlow, setIdFlow] = useState(null);

    const [codeFiles, setCodeFiles] = useState([]);
    const [codeDrawerOpen, setCodeDrawerOpen] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);
    const [openViewCode, setOpenViewCode] = useState(false);
    const [code, setCode] = useState('');

    const [toolsFiles, setToolsFiles] = useState(false);
    const [toolsDrawerOpen, setToolsDrawerOpen] = useState(false);

    const [openCodeEditor, setOpenCodeEditor] = useState(false);

    const [incluirCodigo, setIncluirCodigo] = useState(false);

    const [selectingCodeFor, setSelectingCodeFor] = useState(null); // { nodeId, side }
    const [availableCodes, setAvailableCodes] = useState([]);

    const [tempRegex, setTempRegex] = useState('');
    const [tempVariable, setTempVariable] = useState('');

    const [rightMenuOpen, setRightMenuOpen] = useState(false);

    const closeBox = () => {
        setDrawerOpen(false);
    };

    useEffect(() => {
        if (!nodes.length) return;

        setNodes((nds) =>
            nds.map((n) => ({
                ...n,
                data: {
                    name: n.data?.name || `Caixa ${n.id}`,
                    previewText: n.data?.previewText || '',
                    allowTarget: n.id !== 'main',
                    waitForUser: n.data?.waitForUser ?? false,
                    children: {
                        entrada: n.data?.children?.entrada || [],
                        processamento: n.data?.children?.processamento || [],
                        saida: n.data?.children?.saida || []
                    }
                }
            }))
        );
    }, [loading]);

    //Manipuladores de nó/aresta
    const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);

    // }, []);
    const onConnect = useCallback((params) => {
        if (!params.source || !params.target) return;
        if (!params.sourceHandle) return;
        if (params.source === params.target) return;
        if (params.target === 'main') return;

        setEdges((eds) =>
            addEdge(
                {
                    ...params,
                    type: 'custom'
                },
                eds
            )
        );
    }, []);

    // Add a new node (child starts with 1 saída)
    const addNode = () => {
        const id = uid();

        const newNode = {
            id,
            type: 'message',
            position: { x: Math.random() * 400 + 50, y: Math.random() * 200 + 50 },
            data: {
                name: `Node ${id}`,
                previewText: '',
                allowTarget: true,

                // 🔹 NOVO (Botpress-like)
                // onEnter: [],
                // onReceive: {
                //     waitForUser: false,
                //     actions: []
                // },
                // transitions: [],

                // 🔹 mantém o que você já tinha
                children: {
                    entrada: [],
                    processamento: [],
                    saida: [{ id: uid(), type: 'text', text: 'Novo fluxo' }]
                }
            }
        };

        setNodes((nds) => [...nds, newNode]);
        setSelectedNode(id);
        setDrawerOpen(true);
    };

    // Abrir gaveta ao clicar no nó
    const onNodeClick = (evt, node) => {
        setSelectedNode(node.id);
        setDrawerOpen(true);
        setEditingName(node.data?.name || '');
    };

    // Update node data helpers
    const updateNode = (id, partialData) => {
        setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...partialData } } : n)));
    };

    const updateNodeChildren = (id, children) => {
        setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, children } } : n)));
    };

    const updateChild = (nodeId, side, childId, patch) => {
        setNodes((prev) =>
            prev.map((node) => {
                if (node.id !== nodeId) return node;

                const list = node.data?.children?.[side] || [];
                return {
                    ...node,
                    data: {
                        ...node.data,
                        children: {
                            ...node.data.children,
                            [side]: list.map((child) => (child.id === childId ? { ...child, ...patch } : child))
                        }
                    }
                };
            })
        );
    };

    // Modal flow: open choose modal to decide text/action
    const openAddChildModal = (nodeId, side) => {
        setModal({ nodeId, side });
        setModalType('choose');
        setTempText('');
    };

    const openAddCodigoModal = () => {
        setIncluirCodigo(true);
    };

    const handleChooseType = (type) => {
        setModalType(type); // 'text' or 'action'
        if (type === 'text') setTempText('');
        if (type === 'ai_agent') setAiTempText('');
    };

    const saveAiTextChild = () => {
        if (!modal) return;
        const nodeId = modal.nodeId;
        const side = modal.side; // 'entrada' or 'saida'
        const newChild = { id: uid(), type: 'text', text: tempText || '...' }; //waitForUser: false,
        setNodes((nds) =>
            nds.map((n) =>
                n.id === nodeId
                    ? { ...n, data: { ...n.data, children: { ...n.data.children, [side]: [...(n.data.children[side] || []), newChild] } } }
                    : n
            )
        );
        // keep drawer open and go back to drawer view
        setModal(null);
        setModalType('ai_agent');
    };

    const saveTextChild = () => {
        if (!modal) return;
        const nodeId = modal.nodeId;
        const side = modal.side; // 'entrada' or 'saida'
        const newChild = { id: uid(), type: 'text', text: tempText || '...' }; //waitForUser: false,
        setNodes((nds) =>
            nds.map((n) =>
                n.id === nodeId
                    ? { ...n, data: { ...n.data, children: { ...n.data.children, [side]: [...(n.data.children[side] || []), newChild] } } }
                    : n
            )
        );
        // keep drawer open and go back to drawer view
        setModal(null);
        setModalType('choose');
    };

    const saveRegexChild = () => {
        if (!modal) return;

        const newChild = {
            id: uid(),
            type: 'regex',
            pattern: tempRegex,
            variable: tempVariable
        };

        setNodes((nds) =>
            nds.map((n) =>
                n.id === modal.nodeId
                    ? {
                          ...n,
                          data: {
                              ...n.data,
                              children: {
                                  ...n.data.children,
                                  [modal.side]: [...(n.data.children[modal.side] || []), newChild]
                              }
                          }
                      }
                    : n
            )
        );

        setTempRegex('');
        setTempVariable('');
        setModal(null);
    };

    const addHandoff = (nodeId) => {
        const itemId = uid();

        setNodes((prev) =>
            prev.map((node) => {
                if (node.id !== nodeId) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        children: {
                            ...node.data.children,
                            saida: [
                                ...(node.data.children.saida || []),
                                {
                                    id: itemId,
                                    type: 'handoff',
                                    target: ''
                                }
                            ]
                        }
                    }
                };
            })
        );

        setHandoffEditing({ nodeId, itemId });
        setModalType('handoff');
    };

    const saveActionChild = (actionKey) => {
        if (!modal) return;

        var newChild = {};

        if (actionKey === 'codigo') {
            setSelectingCodeFor({
                nodeId: modal.nodeId,
                side: modal.side
            });

            loadAvailableCodes();
            setModalType('codigo');
            return;
        } else if (actionKey === 'handoff') {
            addHandoff(modal.nodeId);
            return;
        } else if (actionKey === 'ai_agent') {
            setModalType('ai_agent');
            addHandoff(modal.nodeId);
            newChild = {
                id: uid(),
                type: 'ai_agent',
                config: {
                    mode: 'auto',
                    use_tools: true,
                    system_prompt: ''
                }
            };
        }

        if (actionKey !== 'ai_agent') {
            newChild = {
                id: uid(),
                type: 'action',
                action: actionKey
            };
        }

        setNodes((nds) =>
            nds.map((n) =>
                n.id === modal.nodeId
                    ? {
                          ...n,
                          data: {
                              ...n.data,
                              children: {
                                  ...n.data.children,
                                  [modal.side]: [...(n.data.children[modal.side] || []), newChild]
                              }
                          }
                      }
                    : n
            )
        );

        setModal(null);
        setModalType('choose');
    };

    const removeChild = (nodeId, side, childId) => {
        const handleId = `${nodeId}-out-${childId}`;

        setNodes((nds) =>
            nds.map((n) =>
                n.id === nodeId
                    ? {
                          ...n,
                          data: {
                              ...n.data,
                              children: {
                                  ...n.data.children,
                                  [side]: n.data.children[side].filter((c) => c.id !== childId)
                              }
                          }
                      }
                    : n
            )
        );

        setEdges((eds) => eds.filter((e) => e.sourceHandle !== handleId));
    };

    // Edit node name or preview
    const saveNodeName = () => {
        if (!selectedNode) return;
        updateNode(selectedNode, { name: editingName });
    };

    const savePreviewText = (text) => {
        if (!selectedNode) return;
        updateNode(selectedNode, { previewText: text });
    };

    // Delete node (prevent deleting main)
    const deleteNode = (nodeId) => {
        if (nodeId === 'main') return;
        setNodes((nds) => nds.filter((n) => n.id !== nodeId));
        setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
        setDrawerOpen(false);
        setSelectedNode(null);
    };

    // Utility to get node by id
    const getNode = (id) => nodes.find((n) => n.id === id);

    const gravaFluxo = async (nodes, edges) => {
        // if (!idFlow) return;
        try {
            const payload = {
                nodes: nodes,
                edges: edges
            };

            console.log(`gravaFluxo - idFlow: ${idFlow}`);
            console.log(`gravaFluxo - url: v1/apps/${appId}/chatbots/${id}/flows/${idFlow}/`);

            const res = await api.put(`v1/apps/${appId}/chatbots/${id}/flows/${idFlow}/`, payload);
            if (res.data) {
                console.log(`gravaFluxo - fluxo gravado`);
            } else {
                console.log(`gravaFluxo - erro ao gravar fluxo`);
            }
        } catch (err) {
            console.error(`gravaFluxo - erro: ${JSON.stringify(err)}`);
        }
    };

    //? MUDAR COR DO BOTÃO DE SALVAR PARA VERDE QUANDO HOUVER MUDANÇAS NÃO SALVAS (COMPARAR NODES/EDGES ATUAL COM OS CARREGADOS)
    useEffect(() => {
        setColorSave(true);
    }, [nodes, edges]);

    const handleGravaFluxo = async () => {
        gravaFluxo(nodes, edges);
        setColorSave(false);
    };

    const nodesWithHandlers = useMemo(() => {
        return nodes.map((node) => ({
            ...node,
            data: {
                ...node.data,
                onUpdateChildren: updateNodeChildren
            }
        }));
    }, [nodes]);

    const handleOpenCodeDrawer = () => {
        console.log('abrindo code drawer');
        setOpenCodeEditor(true);
    };

    const loadAvailableCodes = async () => {
        const res = await api.get(`v1/apps/${appId}/chatbots/${id}/flows/${idFlow}/actions/`);
        setAvailableCodes(res.data || []);
    };

    const updateHandoffTarget = (nodeId, itemId, value) => {
        setNodes((prev) =>
            prev.map((node) => {
                if (node.id !== nodeId) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        children: {
                            ...node.data.children,
                            saida: node.data.children.saida.map((child) => (child.id === itemId ? { ...child, target: value } : child))
                        }
                    }
                };
            })
        );

        setModal(null);
        setHandoffEditing(null);
    };

    return (
        <Box>
            <div style={{ width: '100%', height: '85vh', position: 'relative', fontFamily: 'Inter, Arial' }}>
                {/* <div style={{ display: 'flex', width: '100%', height: '100%' }}>     */}
                {/* Left-top controls */}

                {/* <div style={{ position: 'absolute', zIndex: 50, top: 12, left: 12, display: 'flex', gap: 8 }}>
                    <ButtonGroup variant="outlined" aria-label="Basic button group">
                        <Button onClick={addNode} sx={{ color: classes.bpNode.color }}>
                            Criar caixa
                        </Button>
                        <Button
                            onClick={() => {
                                handleGravaFluxo();
                            }}
                            sx={{ color: classes.bpNode.color, backgroundColor: colorSave === true ? '#22c55e' : 'transparent' }} //transparent //#3b82f6 //#22c55e
                        >
                            Salvar Fluxo
                        </Button>
                        <Button sx={{ color: classes.bpNode.color }} onClick={() => setCodeDrawerOpen(true)}>
                            Códigos
                        </Button>
                        <Button sx={{ color: classes.bpNode.color }} onClick={() => setToolsDrawerOpen(true)}>
                            Ações
                        </Button>
                    </ButtonGroup>
                </div> */}

                {/*  SIDEBAR FIXA (fora do flow) */}
                <Box
                    onMouseEnter={() => setRightMenuOpen(true)}
                    onMouseLeave={() => setRightMenuOpen(false)}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: rightMenuOpen ? '120px' : '40px',
                        backgroundColor: 'background.paper',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        transition: 'width 0.25s ease',
                        zIndex: 999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '10px',
                        gap: 1
                    }}
                >
                    <Button
                        onClick={() => {
                            handleGravaFluxo();
                        }}
                        sx={{ minWidth: 0, flexDirection: 'column' }}
                    >
                        💾
                        {rightMenuOpen && <span>Salvar</span>}
                    </Button>

                    <Button onClick={() => setToolsDrawerOpen(true)} sx={{ minWidth: 0, flexDirection: 'column' }}>
                        ⚙️
                        {rightMenuOpen && <span>Ações</span>}
                    </Button>

                    <Button onClick={() => setCodeDrawerOpen(true)} sx={{ minWidth: 0, flexDirection: 'column' }}>
                        🧩
                        {rightMenuOpen && <span>Códigos</span>}
                    </Button>

                    <Button onClick={addNode} sx={{ minWidth: 0, flexDirection: 'column' }}>
                        ➕{rightMenuOpen && <span>Novo</span>}
                    </Button>
                </Box>

                {/* Canvas area */}
                <div
                    style={{
                        width: rightMenuOpen ? 'calc(100% - 120px)' : 'calc(100% - 40px)',
                        height: '100%',
                        marginLeft: rightMenuOpen ? '120px' : '40px', // 🔥 ESSENCIAL
                        transition: 'all 0.25s ease'
                    }}
                >
                    <ReactFlow
                        nodes={nodesWithHandlers}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        fitView
                    >
                        {/* <MiniMap /> */}
                        <Controls />
                        <Background variant="dots" gap={12} size={1} />
                    </ReactFlow>
                </div>

                {/* Drawer area (right) */}
                <Drawer anchor="left" open={drawerOpen} onClose={closeBox} PaperProps={{ sx: { width: '40vw', p: 2 } }}>
                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            width: '40vw',
                            height: '100%',
                            background: classes.bpNode.background,
                            borderRight: '1px solid #e5e5e5',
                            padding: 14,
                            boxSizing: 'border-box',
                            overflowY: 'auto'
                        }}
                    >
                        {!drawerOpen && <div style={{ color: '#666' }}>Clique num node para editar</div>}

                        {drawerOpen && selectedNode && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>{getNode(selectedNode)?.data?.name || 'Sem nome'}</h3>
                                </div>

                                <div style={{ marginTop: 8 }}>
                                    <label style={{ fontSize: 13 }}>Nome</label>
                                    <input
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        onBlur={saveNodeName}
                                        style={{ width: '100%', padding: 6, marginTop: 6 }}
                                    />
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={getNode(selectedNode)?.data?.waitForUser || false}
                                                onChange={(e) => {
                                                    updateNode(selectedNode, {
                                                        waitForUser: e.target.checked
                                                    });
                                                }}
                                            />
                                        }
                                        label="Aguardar resposta do usuário"
                                    />
                                </div>

                                {/* Children columns */}
                                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                                    {/* Entrada */}
                                    <div style={{ flex: 1, background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #ddd' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong>Entrada</strong>
                                            <button onClick={() => openAddChildModal(selectedNode, 'entrada')}>+ Adicionar</button>
                                        </div>

                                        <div style={{ marginTop: 8 }}>
                                            {(getNode(selectedNode)?.data?.children?.entrada || []).map((c) => (
                                                <div key={c.id} style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                                                    <div style={{ fontSize: 13 }}>
                                                        {c.type === 'text' && c.text}
                                                        {c.type === 'action' && `Ação: ${c.action}`}
                                                        {c.type === 'regex' && `Regex → ${c.variable}`}
                                                        {c.type === 'handoff' && (
                                                            <>
                                                                Transferir →
                                                                {switchboards.find((sb) => sb.id === c.target)?.name || 'Não selecionado'}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div style={{ marginTop: 6 }}>
                                                        <button onClick={() => removeChild(selectedNode, 'entrada', c.id)}>Remover</button>
                                                    </div>
                                                </div>
                                            ))}
                                            {(getNode(selectedNode)?.data?.children?.entrada || []).length === 0 && (
                                                <div style={{ color: '#888', marginTop: 6 }}>Sem itens</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Processamento */}
                                    <div style={{ flex: 1, background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #ddd' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong>Processamento</strong>
                                            <button onClick={() => openAddChildModal(selectedNode, 'processamento')}>+ Adicionar</button>
                                        </div>

                                        <div style={{ marginTop: 8 }}>
                                            {(getNode(selectedNode)?.data?.children?.processamento || []).map((c) => (
                                                <div key={c.id} style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                                                    <div style={{ fontSize: 13 }}>
                                                        {c.type === 'text' && c.text}
                                                        {c.type === 'action' && `Ação: ${c.action}`}
                                                        {c.type === 'regex' && `Regex → ${c.variable}`}
                                                        {c.type === 'handoff' && (
                                                            <>
                                                                Transferir →
                                                                {switchboards.find((sb) => sb.id === c.target)?.name || 'Não selecionado'}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div style={{ marginTop: 6 }}>
                                                        <button onClick={() => removeChild(selectedNode, 'processamento', c.id)}>
                                                            Remover
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {(getNode(selectedNode)?.data?.children?.processamento || []).length === 0 && (
                                                <div style={{ color: '#888', marginTop: 6 }}>Sem itens</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Saída */}
                                    <div style={{ flex: 1, background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #ddd' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong>Saída</strong>
                                            <button onClick={() => openAddChildModal(selectedNode, 'saida')}>+ Adicionar</button>
                                        </div>

                                        <div style={{ marginTop: 8 }}>
                                            {(getNode(selectedNode)?.data?.children?.saida || []).map((c) => (
                                                <div key={c.id} style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                                                    <div style={{ fontSize: 13 }}>
                                                        {c.type === 'text' && c.text}
                                                        {c.type === 'action' && `Ação: ${c.action}`}
                                                        {c.type === 'regex' && `Regex → ${c.variable}`}
                                                        {c.type === 'handoff' && (
                                                            <>
                                                                Transferir →
                                                                {switchboards.find((sb) => sb.id === c.target)?.name || 'Não selecionado'}
                                                            </>
                                                        )}
                                                    </div>

                                                    <div style={{ marginTop: 8 }}>
                                                        <label style={{ fontSize: 12, color: '#666' }}>Condição (opcional)</label>
                                                        <input
                                                            value={c.condition || ''}
                                                            onChange={(e) =>
                                                                updateChild(selectedNode, 'saida', c.id, { condition: e.target.value })
                                                            }
                                                            placeholder={`Ex: event.payload.text === 'Sim'`}
                                                            style={{ width: '100%', padding: 6, marginTop: 6 }}
                                                        />
                                                    </div>

                                                    <div style={{ marginTop: 6 }}>
                                                        <button onClick={() => removeChild(selectedNode, 'saida', c.id)}>Remover</button>
                                                        <span
                                                            style={{ marginLeft: 8, color: '#666', fontSize: 12 }}
                                                        >{`Fluxo: ${selectedNode}-out-${c.id}`}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {(getNode(selectedNode)?.data?.children?.saida || []).length === 0 && (
                                                <div style={{ color: '#888', marginTop: 6 }}>Sem itens</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Modal overlay area */}
                        {modal && (
                            <div
                                style={{
                                    position: 'fixed',
                                    right: '40vw',
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    background: 'rgba(0,0,0,0.35)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 100
                                }}
                            >
                                <div style={{ width: 420, background: '#fff', padding: 16, borderRadius: 8 }}>
                                    {modalType === 'choose' && (
                                        <>
                                            <h4>Adicionar em {modal.side}</h4>
                                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                <button onClick={() => handleChooseType('text')}>Texto</button>
                                                <button onClick={() => handleChooseType('ai_agent')}>🤖 I.A</button>
                                                <button onClick={() => handleChooseType('action')}>Ação</button>
                                                <button onClick={() => handleChooseType('regex')}>Regex / Capturar variável</button>
                                                <button
                                                    onClick={() => {
                                                        setModal(null);
                                                        setModalType('choose');
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {modalType === 'text' && (
                                        <>
                                            <h4>Adicionar Texto</h4>
                                            <textarea
                                                value={tempText}
                                                onChange={(e) => setTempText(e.target.value)}
                                                style={{ width: '100%', minHeight: 120 }}
                                            />
                                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                <button onClick={saveTextChild}>Salvar Texto</button>
                                                <button onClick={() => setModalType('choose')}>Voltar</button>
                                            </div>
                                        </>
                                    )}

                                    {modalType === 'ai_agent' && (
                                        <>
                                            <h4>Adicionar Texto</h4>
                                            <textarea
                                                value={tempText}
                                                onChange={(e) => setTempText(e.target.value)}
                                                style={{ width: '100%', minHeight: 120 }}
                                            />
                                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                                <button onClick={saveTextChild}>Salvar Texto</button>
                                                <button onClick={() => setModalType('choose')}>Voltar</button>
                                            </div>
                                        </>
                                    )}

                                    {modalType === 'action' && (
                                        <>
                                            <h4>Adicionar Ação (exemplo)</h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <button onClick={() => saveActionChild('send_message')}>Enviar Mensagem</button>
                                                <button onClick={() => saveActionChild('codigo')}>Inserir Código</button>
                                                <button onClick={() => saveActionChild('handoff')}>Transferir conversa</button>
                                                <button onClick={() => saveActionChild('ai_agent')}>🤖 IA (Auto)</button>
                                                {/* <button onClick={() => addHandoff(nodeId)}>Transferir para humano</button> */}
                                                {/* <button onClick={() => saveActionChild('call_api')}>Chamar API</button> */}
                                                {/* <button onClick={() => saveActionChild('condition')}>Condição</button> */}
                                                {/* <button onClick={() => saveActionChild('end_flow')}>Finalizar Fluxo</button> */}
                                                <button onClick={() => setModalType('choose')}>Voltar</button>
                                            </div>
                                        </>
                                    )}

                                    {modalType === 'regex' && (
                                        <>
                                            <h4>Capturar variável (Regex)</h4>

                                            <label>Padrão Regex</label>
                                            <input
                                                value={tempRegex}
                                                onChange={(e) => setTempRegex(e.target.value)}
                                                placeholder="^\d{11}$"
                                                style={{ width: '100%', marginBottom: 8 }}
                                            />

                                            <label>Nome da variável</label>
                                            <input
                                                value={tempVariable}
                                                onChange={(e) => setTempVariable(e.target.value)}
                                                placeholder="cpf"
                                                style={{ width: '100%', marginBottom: 12 }}
                                            />

                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button onClick={saveRegexChild}>Salvar</button>
                                                <button onClick={() => setModalType('choose')}>Voltar</button>
                                            </div>
                                        </>
                                    )}

                                    {modalType === 'codigo' && (
                                        <>
                                            <h4>Selecionar código</h4>

                                            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                                {availableCodes.map((code) => (
                                                    <div
                                                        key={code.id}
                                                        onClick={() => {
                                                            const newChild = {
                                                                id: uid(),
                                                                type: 'action',
                                                                action: 'codigo',
                                                                code_id: code.id,
                                                                code_name: code.name
                                                            };

                                                            setNodes((nds) =>
                                                                nds.map((n) =>
                                                                    n.id === selectingCodeFor.nodeId
                                                                        ? {
                                                                              ...n,
                                                                              data: {
                                                                                  ...n.data,
                                                                                  children: {
                                                                                      ...n.data.children,
                                                                                      [selectingCodeFor.side]: [
                                                                                          ...(n.data.children[selectingCodeFor.side] || []),
                                                                                          newChild
                                                                                      ]
                                                                                  }
                                                                              }
                                                                          }
                                                                        : n
                                                                )
                                                            );

                                                            setModal(null);
                                                            setModalType('choose');
                                                            setSelectingCodeFor(null);
                                                        }}
                                                        style={{
                                                            padding: 8,
                                                            borderRadius: 6,
                                                            cursor: 'pointer',
                                                            background: '#f4f4f4',
                                                            marginBottom: 6
                                                        }}
                                                    >
                                                        {code.name}.js
                                                    </div>
                                                ))}
                                            </div>

                                            <button onClick={() => setModalType('action')}>Voltar</button>
                                        </>
                                    )}
                                    {modalType === 'handoff' && handoffEditing && (
                                        <>
                                            <h4>Selecionar Transferência</h4>

                                            <select
                                                value={
                                                    getNode(handoffEditing.nodeId)?.data?.children?.saida?.find(
                                                        (c) => c.id === handoffEditing.itemId
                                                    )?.target || ''
                                                }
                                                onChange={(e) =>
                                                    updateHandoffTarget(handoffEditing.nodeId, handoffEditing.itemId, e.target.value)
                                                }
                                                style={{ width: '100%', marginBottom: 12 }}
                                            >
                                                <option value="">Selecione</option>
                                                {switchboards.map((sb) => (
                                                    <option key={sb.id} value={sb.id}>
                                                        {sb.name}
                                                    </option>
                                                ))}
                                            </select>

                                            <button
                                                onClick={() => {
                                                    setModal(null);
                                                    setHandoffEditing(null);
                                                }}
                                            >
                                                Cancelar
                                            </button>
                                        </>
                                    )}
                                    {/* {modalType === 'ai_agent' && (
                                        <>
                                            <h4>Selecionar IA</h4>
                                        </>
                                    )} */}
                                </div>
                            </div>
                        )}
                        <div>
                            {selectedNode !== 'main' && (
                                <ButtonGroup variant="outlined" aria-label="Basic button group">
                                    <Button
                                        onClick={() => deleteNode(selectedNode)}
                                        sx={{ color: 'black', marginLeft: 1, marginTop: 5, backgroundColor: '#f87272' }}
                                    >
                                        Apagar
                                    </Button>
                                    <Button
                                        onClick={() => setDrawerOpen(false)}
                                        sx={{ color: 'black', marginLeft: 1, marginTop: 5, backgroundColor: '#42f566' }}
                                    >
                                        Salvar
                                    </Button>
                                </ButtonGroup>
                            )}
                        </div>
                        <div>
                            {selectedNode === 'main' && (
                                <ButtonGroup variant="outlined" aria-label="Basic button group">
                                    <Button
                                        onClick={() => setDrawerOpen(false)}
                                        sx={{ color: 'black', marginLeft: 1, marginTop: 5, backgroundColor: '#42f566' }}
                                    >
                                        Salvar
                                    </Button>
                                </ButtonGroup>
                            )}
                        </div>
                    </div>
                </Drawer>

                <CodeBuilder open={codeDrawerOpen} onClose={() => setCodeDrawerOpen(false)} idFlow={idFlow} chatbotId={id} />
                <ToolsBuilder
                    open={toolsDrawerOpen}
                    onClose={() => setToolsDrawerOpen(false)}
                    appId={appId}
                    idFlow={idFlow}
                    chatbotId={id}
                />
            </div>
        </Box>
    );
}
